import Anchor from "atoms/anchors/anchor";
import Button from "atoms/buttons/button";
import { ButtonStyles } from "atoms/constants/button-styles";
import { HeadingPriority } from "atoms/constants/heading-priority";
import { IconSizes } from "atoms/constants/icon-sizes";
import { Icons } from "atoms/constants/icons";
import Icon from "atoms/icons/icon";
import RemoteImage from "atoms/images/remote-image";
import Heading from "atoms/typography/heading";
import Paragraph from "atoms/typography/paragraph";
import PublicationRecord from "models/view-models/publication-record";
import UserPublicationFavoriteRecord from "models/view-models/user-publication-favorite-record";
import OfflineBookStatusIcon from "molecules/offline-book-status-icon/offline-book-status-icon";
import Tooltip, { TooltipPlacement } from "molecules/tooltips/tooltip";
import PublicationOptionsMenu from "organisms/modals/publication-selection-modal/publication-options-menu";
import React from "react";
import { CollectionUtils } from "utilities/collection-utils";
import { CustomColorUtils } from "utilities/custom-color-utils";
import { PublicationColor } from "utilities/enumerations/publication-color";
import useOfflineBooks from "utilities/hooks/domain/offline/use-offline-books";
import useOfflineDevice from "utilities/hooks/domain/offline/use-offline-device";
import useUpdatePublicationFavorite from "utilities/hooks/domain/user-publication-favorites/use-update-publication-favorite";
import { useLocalization } from "utilities/hooks/use-localization";
import NumberUtils from "utilities/number-utils";
import BookService from "utilities/services/books/book-service";
import FileService from "utilities/services/file-service";

// -------------------------------------------------------------------------------------------------
// #region Interfaces
// -------------------------------------------------------------------------------------------------

export interface PublicationCardProps {
    publication: PublicationRecord;
    favorites: Array<UserPublicationFavoriteRecord>;
    onChangeFavorites: (
        favorites: Array<UserPublicationFavoriteRecord>
    ) => void;
    showFavoriteIcon?: boolean;
}

// #endregion Interfaces

// -------------------------------------------------------------------------------------------------
// #region Component
// -------------------------------------------------------------------------------------------------

const PublicationCard: React.FC<PublicationCardProps> = (
    props: PublicationCardProps
) => {
    const {
        publication,
        favorites,
        onChangeFavorites,
        showFavoriteIcon = false,
    } = props;

    const CSS_CLASS_NAME = "c-publication-card";

    const anchorId = `publication-card-${publication.id!}`;

    const { get: getFileApi } = FileService.useGetQuery();
    const getFileResult = getFileApi(
        { id: publication.coverImageFileId! },
        undefined,
        { enabled: !NumberUtils.isDefault(publication.coverImageFileId) }
    );
    const imageFile = getFileResult.data?.resultObject;

    const { list: listBooks } = BookService.useListQuery();
    const { data: books } = listBooks({
        code: publication.code,
        edition: publication.edition,
    });
    const { resultObject: currentDevice } = useOfflineDevice();
    const { resultObject: offlineBooks } = useOfflineBooks({
        offlineDeviceId: currentDevice?.id,
    });

    const currentBook = CollectionUtils.first(books?.resultObjects);
    const offlineBook = offlineBooks.find(
        (b) =>
            b.bookId === currentBook?.id &&
            b.offlineDeviceId === currentDevice?.id
    );

    const { unfavorite, isFavorited, addFavorite } =
        useUpdatePublicationFavorite(publication, favorites, onChangeFavorites);

    const { t } = useLocalization();

    const handleUnfavoritePublication = async () => {
        await unfavorite();
    };

    const handleFavoritePublication = async () => {
        await addFavorite();
    };

    const classNames = [CSS_CLASS_NAME];
    if (publication.coverImageFileId == null) {
        classNames.push("-no-image");
    }

    const colorModifier = CustomColorUtils.getClassModifier(
        PublicationColor,
        publication.getColorOrDefault(),
        "publication"
    );
    return (
        <article className={CSS_CLASS_NAME}>
            <header className={`${CSS_CLASS_NAME}__header`}>
                {publication.edition && (
                    <aside className={`${CSS_CLASS_NAME}__edition`}>
                        {publication.edition}
                        {publication.getIsLegacy() && (
                            <Tooltip
                                content={t("readOnly")}
                                cssClassName={``}
                                disabled={false}
                                durationInMs={0}
                                hideOnClick={true}
                                placement={TooltipPlacement.Right}>
                                <div
                                    className={`${CSS_CLASS_NAME}__legacy-icon-container`}>
                                    <Icon
                                        cssClassName={`${CSS_CLASS_NAME}__legacy-icon`}
                                        type={Icons.Scroll}
                                        size={IconSizes.Base}
                                    />
                                </div>
                            </Tooltip>
                        )}
                    </aside>
                )}

                <div className={`${CSS_CLASS_NAME}__menu`}>
                    {showFavoriteIcon && (
                        <Button
                            cssClassName={`${CSS_CLASS_NAME}__favoriteButton`}
                            onClick={
                                isFavorited
                                    ? handleUnfavoritePublication
                                    : handleFavoritePublication
                            }
                            style={ButtonStyles.None}>
                            <Icon
                                type={
                                    isFavorited
                                        ? Icons.StarFilled
                                        : Icons.StarOutline
                                }
                            />
                        </Button>
                    )}
                    <PublicationOptionsMenu
                        favorites={favorites}
                        onChangeFavorites={onChangeFavorites}
                        publication={publication}
                    />
                </div>
            </header>
            <div className={`${CSS_CLASS_NAME}__content`}>
                <div className={`${CSS_CLASS_NAME}__codeAndTitle`}>
                    {publication.code && (
                        <Heading
                            cssClassName={`${CSS_CLASS_NAME}__code`}
                            priority={HeadingPriority.Four}>
                            {publication.getDisplayCode()}
                        </Heading>
                    )}
                    <Paragraph cssClassName={`${CSS_CLASS_NAME}__title`}>
                        <Anchor
                            id={anchorId}
                            dataTestCode={publication.code}
                            dataTestEdition={publication.edition}
                            cssClassName={`${CSS_CLASS_NAME}__link`}
                            to={publication.getRoute()}>
                            {publication.code
                                ? publication.title
                                : publication.getDisplayTitle()}
                        </Anchor>
                    </Paragraph>
                </div>
                <OfflineBookStatusIcon
                    book={currentBook}
                    offlineBook={offlineBook}
                    placeAtBottom={false}
                    showBackground={true}
                />
            </div>
            <footer
                className={`${CSS_CLASS_NAME}__imageContainer ${colorModifier}`}>
                {imageFile != null && <RemoteImage file={imageFile} />}
            </footer>
        </article>
    );
};

// #endregion Component

// -------------------------------------------------------------------------------------------------
// #region Exports
// -------------------------------------------------------------------------------------------------

export default PublicationCard;

// #endregion Exports
